/** /tags — tenant-scoped tag catalog (grid) with QuerySearch rich-search. */
import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Chip } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';

import * as tagsApi from '@/api/tags';
import type { TagCategoryBrief, TagListItem } from '@/api/tags';
import { deleteBatchAction } from '@/components/DataGrid/batchTypes';
import { orderColumns } from '@/components/DataGrid/columnOrder';
import { copyableColumn } from '@/components/DataGrid/copyableColumn';
import {
  ResourceDataGrid,
  type ResourceDataGridParams,
} from '@/components/DataGrid/ResourceDataGrid';
import { ResponsiveFilterBar, type FilterBarItem } from '@/components/DataGrid/ResponsiveFilterBar';
import { PageHeader } from '@/components/PageHeader';
import {
  QuerySearchInput,
  parse,
  serialize,
  toWireAst,
  type FilterNode,
} from '@/components/QuerySearch';
import { useSnackbar } from '@/components/Snackbar';
import { TenantLinkCell } from '@/components/TenantMark/TenantMark';
import { TenantPicker } from '@/components/TenantPicker/TenantPicker';
import { useGridFilters, type FilterDef } from '@/hooks/useGridFilters';
import { useCreateIntent } from '@/hooks/useCreateIntent';
import { usePermissions } from '@/hooks/usePermissions';
import { makeResourceFiltersAdapter, useGridPresets } from '@/hooks/useGridPresets';
import { PAGE_FILL_SX } from '@/layout/pageFill';
import { pageTenantsFrom } from '@/scope/pageTenants';
import { formatTenantIds, singleTenantId, tenantIdsToApi } from '@/scope/tenantIds';
import { useTenantScopeList } from '@/scope/tenantScope';

import { TagCategoryPicker } from './TagCategoryPicker';
import { TagCreateDialog } from './TagCreateDialog';
import { buildTagNamespaces } from './tagsNamespaces';

const digits = (v: string) => (/^\d+$/.test(v) ? Number(v) : undefined);

const FILTERS: FilterDef[] = [
  { param: 'tenant', apiParam: 'tour_operator_id', toApi: tenantIdsToApi, tenantScope: 'multi' },
  { param: 'category', toApi: digits },
];
/** Presets save/restore the `?q=` DSL alongside tenant + category. */
const PRESET_FILTERS = makeResourceFiltersAdapter(FILTERS, ['q']);

const DEFAULT_ORDER = ['id', 'tenant', 'category', 'name', 'custom_id', 'user', 'vehicle', 'updated_ts'];
// `custom_id` is NULL for every operator-authored tag, so it stays off by default
// and is a per-user grid preference for the tenants that run identified categories.
const DEFAULT_HIDDEN = ['vehicle', 'custom_id'];
const fmtDate = (v: string | null | undefined) => (v ? new Date(v).toLocaleDateString() : '—');

export function TagsListPage() {
  const { hasPerm } = usePermissions();
  const canManage = hasPerm('console_tags_manage');
  const [createOpen, setCreateOpen] = useState(false);
  useCreateIntent(() => setCreateOpen(true));
  const snackbar = useSnackbar();
  const [sp, setSp] = useSearchParams();

  const presets = useGridPresets({
    grid: 'tags',
    defaultOrder: DEFAULT_ORDER,
    defaultHidden: DEFAULT_HIDDEN,
    defaultOrdering: 'name',
    filters: PRESET_FILTERS,
  });

  const {
    extra,
    getParam,
    setParam,
    resetAll: resetAllFilters,
    anyActive: anyFilterActive,
  } = useGridFilters(FILTERS, presets.filtersLayer);

  // The operators the effective tenant filter names (URL > preset > scope): the
  // picker's value, and what the create dialog pre-selects / features (D9).
  const pageTenants = pageTenantsFrom(getParam('tenant'), useTenantScopeList());
  const tenantIds = formatTenantIds(pageTenants.map((t) => t.id));

  const categoryParam = getParam('category');
  // A URL-only placeholder until the picker resolves the real row; `false` is the
  // safe default for a flag that only ever unlocks an extra field.
  const categoryValue: TagCategoryBrief | null =
    categoryParam && /^\d+$/.test(categoryParam)
      ? { id: Number(categoryParam), code: '', name: `#${categoryParam}`, external_identity: false }
      : null;

  // ── QuerySearch (`?q=` DSL → wire AST) ─────────────────────────────────────
  const namespaces = useMemo(() => buildTagNamespaces(), []);
  const q = presets.getParam('q') ?? '';
  const { tree, diagnostics } = useMemo(() => {
    const r = parse(q, namespaces);
    return { tree: r.nodes, diagnostics: r.diagnostics };
  }, [q, namespaces]);
  const wire = useMemo(() => toWireAst(tree), [tree]);

  const { beforeUrlWrite } = presets;
  const setQuery = useCallback(
    (nodes: FilterNode[]) => {
      const next = new URLSearchParams(sp);
      beforeUrlWrite(next); // divergence choke point (materializes a loaded preset)
      const str = serialize(nodes);
      if (str) next.set('q', str);
      else next.delete('q');
      setSp(next, { replace: true });
    },
    [sp, setSp, beforeUrlWrite],
  );

  const lastDiagKey = useRef<string>('');
  useEffect(() => {
    if (diagnostics.length === 0) return;
    const key = `${q}::${diagnostics.length}`;
    if (key === lastDiagKey.current) return;
    lastDiagKey.current = key;
    snackbar.show(
      `Some of your search couldn't be interpreted: ${diagnostics[0]!.message}`,
      'warning',
    );
  }, [diagnostics, q, snackbar]);

  // The wire AST rides in `extra.filters` (a JSON string) so it folds into BOTH
  // the react-query key and the batch `filterQuery` — a query change refetches
  // the list AND "delete all matching" scopes to the filtered set (zero grid change).
  const gridExtra = useMemo(
    () => ({ ...extra, ...(wire.length ? { filters: JSON.stringify(wire) } : {}) }),
    [extra, wire],
  );
  // Name and category suggestions come from the tags / tag-categories lists, which
  // take the whole tenant list.
  const extraFilterValues = tenantIds ? { tour_operator_id: tenantIds } : undefined;

  const columns = useMemo<GridColDef<TagListItem>[]>(
    () => [
      copyableColumn({ field: 'id', headerName: 'ID', width: 80 }),
      {
        field: 'tenant',
        headerName: 'Operator',
        width: 150,
        sortable: false,
        valueGetter: (_, row) => row.tenant?.code ?? '',
        renderCell: (p) => (
          <TenantLinkCell
            tenant={p.row.tenant}
            empty={<Chip size="small" variant="outlined" label="system" />}
          />
        ),
      },
      {
        field: 'category',
        headerName: 'Category',
        width: 170,
        sortable: false,
        valueGetter: (_, row) => row.category?.name ?? '',
        renderCell: (p) => (
          <RouterLink
            to={`/tag-categories/${p.row.category.id}`}
            style={{ color: '#0a1a3f' }}
            onClick={(e) => e.stopPropagation()}
          >
            {p.row.category.name}
          </RouterLink>
        ),
      },
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        minWidth: 180,
        renderCell: (p) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <RouterLink
              to={`/tags/${p.row.id}`}
              style={{
                color: '#0a1a3f',
                fontWeight: 500,
                ...(p.row.is_deleted ? { textDecoration: 'line-through', opacity: 0.6 } : {}),
              }}
            >
              {p.row.name}
            </RouterLink>
            {p.row.is_deleted && (
              <Chip size="small" variant="outlined" color="default" label="deleted" />
            )}
          </Box>
        ),
      },
      // Serialized, ordering-whitelisted and filterable server-side (`custom_id`
      // namespace), so the column is fully sortable/copyable.
      copyableColumn({ field: 'custom_id', headerName: 'External ID', width: 160 }),
      {
        field: 'user',
        headerName: 'User',
        width: 200,
        sortable: false,
        valueGetter: (_, row) => row.user?.email ?? row.user?.name ?? '',
        renderCell: (p) =>
          p.row.user ? p.row.user.name || p.row.user.email || `#${p.row.user.id}` : '—',
      },
      {
        field: 'vehicle',
        headerName: 'Vehicle',
        width: 150,
        sortable: false,
        valueGetter: (_, row) => row.vehicle?.name ?? '',
        renderCell: (p) => (p.row.vehicle ? p.row.vehicle.name : '—'),
      },
      {
        field: 'updated_ts',
        headerName: 'Updated',
        width: 130,
        valueGetter: (_, row) => row.updated_ts,
        renderCell: (p) => fmtDate(p.row.updated_ts),
      },
    ],
    [],
  );

  const orderingFallback = presets.ordering;
  // Shared by the page query and the count-only call, so the total can never
  // describe a different filter set than the rows.
  const listParams = useCallback(
    (params: ResourceDataGridParams) => ({
      ordering: params.ordering ?? orderingFallback ?? 'name',
      // extra carries tour_operator_id / category / filters (the wire AST).
      ...(params.extra as Record<string, never>),
      ...(params.timeoutTier ? { timeout: params.timeoutTier } : {}),
    }),
    [orderingFallback],
  );

  const queryFn = useCallback(
    (params: ResourceDataGridParams) =>
      tagsApi.list({
        ...listParams(params),
        page: params.page,
        pageSize: params.pageSize,
        ...(params.countMode ? { count_mode: params.countMode } : {}),
      }),
    [listParams],
  );

  const countOnly = useCallback(
    (params: ResourceDataGridParams) => tagsApi.count(listParams(params)),
    [listParams],
  );

  const anyActive = anyFilterActive || q.trim().length > 0;
  const resetAll = useCallback(() => {
    resetAllFilters(); // clears tenant + category
    setQuery([]); // clears ?q=
  }, [resetAllFilters, setQuery]);

  const toolbarItems: FilterBarItem[] = [
    {
      key: 'tenant',
      priority: 5,
      active: pageTenants.length > 0,
      node: (
        <Box sx={{ minWidth: 280 }}>
          <TenantPicker
            multiple
            value={pageTenants}
            onChange={(next) => setParam('tenant', formatTenantIds(next.map((t) => t.id)))}
          />
        </Box>
      ),
    },
    {
      key: 'category',
      priority: 4,
      active: categoryValue !== null,
      node: (
        <Box sx={{ minWidth: 260 }}>
          <TagCategoryPicker
            value={categoryValue}
            // One tenant's categories; with several (or none), every category.
            tourOperatorId={singleTenantId(tenantIds)}
            onChange={(c) => setParam('category', c ? String(c.id) : null)}
          />
        </Box>
      ),
    },
  ];

  return (
    <Box sx={PAGE_FILL_SX}>
      <PageHeader
        helpStory="tags-editor"
        title="Tags"
        subtitle="Tenant-scoped tags. Edit each tag's data against its category schema."
        actions={
          canManage && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
              New tag
            </Button>
          )
        }
      />

      <TagCreateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        pageTenants={pageTenants}
      />

      <ResponsiveFilterBar items={toolbarItems} />

      <Box sx={{ mb: 2 }}>
        <QuerySearchInput
          namespaces={namespaces}
          value={tree}
          onChange={setQuery}
          {...(extraFilterValues ? { extraFilterValues } : {})}
          placeholder="Search tags…  e.g. category=checkin & custom_id=~a7a5"
        />
      </Box>

      <ResourceDataGrid<TagListItem>
        queryKey={['tags']}
        queryFn={queryFn}
        countMode={{ grid: 'tags', countOnly }}
        allowExtendedTimeout
        columns={orderColumns(columns, presets.orderedFields)}
        extra={gridExtra}
        batchResource="tags"
        batchActions={[deleteBatchAction('console_tags_manage')]}
        defaultSort={{ field: 'name', sort: 'asc' }}
        sortModel={presets.sortModel}
        onSortModelChange={presets.onSortModelChange}
        columnVisibilityModel={presets.columnVisibilityModel}
        onColumnVisibilityModelChange={presets.onColumnVisibilityModelChange}
        onColumnOrderChange={presets.onColumnOrderChange}
        emptyMessage="No tags yet."
        headerToolbar={{
          onResetAllFilters: resetAll,
          resetDisabled: !anyActive,
          exportFileName: 'tags',
          presets: presets.toolbar,
        }}
      />
    </Box>
  );
}
